import { useState } from "react";
import Swal from "sweetalert2";
import generarLineaCaptura from "../utilities/GenerarLineaCaptura";
import { API_BASE, PINPAD_URL } from "../config";
import { asegurarVersionVigente } from "../utilities/versionApp";
import { plantelOficialDesdeIdBD } from "../utilities/plantel";
import { apiFetch } from "../utilities/apiFetch";
import { enviarConfirmandoRepetida } from "../utilities/autorizacionTPV";

const useCobroAspirantes = (user) => {
  const [folioBusqueda, setFolioBusqueda] = useState("");
  const [aspiranteInfo, setAspiranteInfo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mostrarModalValidacion, setMostrarModalValidacion] = useState(false);
  const [folioBanco, setFolioBanco] = useState("");
  const [bancoSeleccionado, setBancoSeleccionado] = useState("");
  const [loadingValidacion, setLoadingValidacion] = useState(false);

  const [modalExito, setModalExito] = useState({
    visible: false, nombre: "", monto: 0, autorizacion: "", referencia: ""
  });

  const buscarAspirantePorFolio = async (e) => {
    if (e) e.preventDefault();
    if (!folioBusqueda.trim()) {
      Swal.fire("Atención", "Por favor ingresa un número de folio.", "warning");
      return;
    }

    setCargando(true);
    setAspiranteInfo(null);
    try {
      const respuesta = await apiFetch(
        `${API_BASE}/wMultipagosObtenerAspirante.php?folio=${folioBusqueda}&id_plantel=${user?.id_plantel}`
      );
      const resultado = await respuesta.json();

      if (resultado.success && resultado.data) {
        const d = resultado.data;

        const infoFormateada = {
          id: d.id_registro,
          nombre_completo: `${d.nombre} ${d.apellido_paterno} ${d.apellido_materno}`.trim(),
          pre_strfolio: d.matricula,
          curp: d.curp || "Sin registro",
          plantel: d.nombre_plantel || "No identificado",
          id_plantel_aspirante: d.id_plantel_aspirante || null,
          aula_externa: d.nombre_aula_externa || "No aplica",
          carrera: d.nombre_carrera || "Sin carrera asignada",

          pre_validapago: (d.pagado === "t" || d.pagado === true || d.pagado === "1") ? "1" : "0",

          pre_intfolio_banco: d.folio_banco || "S/F",
          referencia_pagada: d.referencia_pagada || "No identificada",
          fecha_pago: d.fecha_pago || "Sin fecha",
          nombre_banco: d.nombre_banco || "",
          nombre_origen: d.nombre_origen || "",
          importe_pagado: d.importe_pagado || null,

          referencias: [
            { banco: "bancomer", referencia: d.ref_bancomer, monto: d.concepto_pago?.precio || "600.00" },
            { banco: "banorte", referencia: d.ref_banorte, monto: d.concepto_pago?.precio || "600.00" }
          ]
        };

        setAspiranteInfo(infoFormateada);
      } else {
        Swal.fire("No encontrado", resultado.message || "No se encontró ningún aspirante con ese folio en tu plantel.", "info");
      }
    } catch (error) {
      console.error("Error al buscar aspirante:", error);
      Swal.fire("Error de conexión", "Hubo un error al conectar con el servidor.", "error");
    } finally {
      setCargando(false);
    }
  };

  // Devuelve la referencia oficial segun el metodo elegido
  const obtenerReferenciaPorMetodo = (metodo) => {
    if (!aspiranteInfo?.referencias) return "";

    const buscar = (nombre) => {
      const r = aspiranteInfo.referencias.find(
        x => String(x.banco || "").toLowerCase() === nombre
      );
      return r ? r.referencia : null;
    };

    // 2 = BBVA (bancomer). Todo lo demas (Banorte, TPV, Pinpad) usa Banorte.
    const ref = metodo === "2" ? buscar("bancomer") : buscar("banorte");
    return ref || ("0110" + aspiranteInfo.pre_strfolio);
  };

  const abrirModalValidacion = () => {
    setBancoSeleccionado("");
    setFolioBanco("");
    setMostrarModalValidacion(true);
  };

  const cerrarModal = () => {
    setMostrarModalValidacion(false);
    setFolioBanco("");
    setBancoSeleccionado("");
  };

  const confirmarValidacionPago = async (discrepancia = null) => {
    if (!folioBanco.trim() || !bancoSeleccionado) {
      Swal.fire("Atención", "Selecciona dónde se realizó el pago e ingresa el folio.", "warning");
      return;
    }

    setLoadingValidacion(true);
    try {
      const payload = {
        id_preinscripcion: aspiranteInfo.id,
        id_cajero: user?.id_usuario,
        folio_banco: folioBanco,
        metodo_pago: bancoSeleccionado,
        referencia: obtenerReferenciaPorMetodo(bancoSeleccionado),
        discrepancia: discrepancia
      };

      // Si la autorizacion TPV ya esta en otra orden, el servidor pide confirmacion antes de guardar
      const resultado = await enviarConfirmandoRepetida(async (confirmada) => {
        const respuesta = await apiFetch(`${API_BASE}/wMultipagosValidarPagoAspirante.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload, confirmar_autorizacion_repetida: confirmada })
        });
        return respuesta.json();
      });
      if (!resultado) return; // el cajero va a revisar el ticket; el modal sigue abierto

      if (resultado.success) {
        Swal.fire({
          title: "¡Pago de Aspirante Validado!",
          text: resultado.message,
          icon: resultado.modo_prueba ? "info" : "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: "#28a745",
          didOpen: () => {
            const cont = document.querySelector('.swal2-container');
            if (cont) cont.style.zIndex = '99999';
          }
        });

        cerrarModal();
        buscarAspirantePorFolio();
      } else {
        Swal.fire({
          title: "No se pudo validar",
          text: resultado.message || "Ocurrió un problema al procesar el pago.",
          icon: "error",
          didOpen: () => {
            const cont = document.querySelector('.swal2-container');
            if (cont) cont.style.zIndex = '99999';
          }
        });
      }
    } catch (error) {
      console.error("Error al validar pago de aspirante:", error);
      Swal.fire("Error crítico", "Hubo un error al procesar la validación.", "error");
    } finally {
      setLoadingValidacion(false);
    }
  };

  const procesarCobroPinpad = async () => {
    if (!aspiranteInfo) return;

    setLoadingValidacion(true);

    try {
// El plantel en BD va corrido en 2: BD 03 = plantel 01 Tesistan
      // El plantel de la referencia es el del ASPIRANTE, no el del cajero
      if (!aspiranteInfo.id_plantel_aspirante) {
        Swal.fire("Datos incompletos", "No se pudo identificar el plantel del aspirante.", "error");
        return;
      }

      // BD va corrido en 2: BD 04 = plantel 02 Tlaquepaque
      const idPlantelBanco = plantelOficialDesdeIdBD(aspiranteInfo.id_plantel_aspirante);
      const montoFicha = parseFloat(aspiranteInfo.referencias?.[0]?.monto || 600);

      // Mismo motor que alumnos y externos: el folio ocupa el lugar de la matricula
      const infoCaptura = generarLineaCaptura(
        idPlantelBanco,
        ["10"],                        // clave del concepto de ficha
        aspiranteInfo.pre_strfolio,
        montoFicha
      );

      const referenciaBase = infoCaptura.banorte.referenciaBase;   // 26 caracteres
      const sufijoTiempo = Date.now().toString().slice(-4);
      const referenciaTerminal = referenciaBase + sufijoTiempo;    // 30 caracteres

      // Antes de pasar la tarjeta: si hay una version nueva publicada, no cobrar
      if (!(await asegurarVersionVigente())) return;

      const resPinpad = await fetch(PINPAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Monto: montoFicha,
          Referencia: referenciaTerminal,
          Concepto: "PAGO FICHA ADMISION"
        })
      });

      const dataPinpad = await resPinpad.json();

      if (dataPinpad.status !== 'Aprobado') {
        Swal.fire("Pago Declinado", `La terminal reportó: ${dataPinpad.mensaje}`, "error");
        return;
      }

      const payload = {
        id_preinscripcion: aspiranteInfo.id,
        id_cajero: user?.id_usuario,
        folio_banco: dataPinpad.autorizacion,
        metodo_pago: "4",
        referencia: referenciaTerminal
      };

      const respuesta = await apiFetch(`${API_BASE}/wMultipagosValidarPagoAspirante.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const resultado = await respuesta.json();

      if (resultado.success) {
        setModalExito({
          visible: true,
          nombre: aspiranteInfo.nombre_completo,
          monto: montoFicha,
          autorizacion: dataPinpad.autorizacion || "N/A",
          referencia: referenciaTerminal
        });
        buscarAspirantePorFolio();
      } else {
        Swal.fire(
          "Atención: cobro retenido",
          `El pago SÍ pasó en la terminal (autorización ${dataPinpad.autorizacion}), pero hubo un error al guardar: ${resultado.message}`,
          "warning"
        );
      }

    } catch (error) {
      console.error("Error al procesar Pinpad:", error);
      Swal.fire("Error de Terminal", "No se pudo conectar con la Pinpad. Verifica que la aplicación local esté abierta.", "error");
    } finally {
      setLoadingValidacion(false);
    }
  };

  const limpiarModulo = () => {
    setFolioBusqueda("");
    setAspiranteInfo(null);
  };

  return {
    folioBusqueda, setFolioBusqueda, aspiranteInfo, cargando,
    mostrarModalValidacion, setMostrarModalValidacion,
    folioBanco, setFolioBanco, bancoSeleccionado, setBancoSeleccionado,
    loadingValidacion, buscarAspirantePorFolio, abrirModalValidacion,
    confirmarValidacionPago, limpiarModulo, procesarCobroPinpad,
    modalExito, setModalExito, cerrarModal
  };
};

export default useCobroAspirantes;